declare const zip: any;
import { Vec3 } from 'playcanvas';
import { Events } from './events';
import { localize } from './ui/localization';

// Helper to determine file types
const isImageFile = (filename: string) => /\.(png|jpg|jpeg|webp|gif|bmp)$/i.test(filename);

// Non-cryptographic fallback for when SubtleCrypto is unavailable. Two independent
// 32-bit accumulators concatenated to 16 hex chars, matching the length of the
// truncated SHA-256 below.
const fallbackHash = (bytes: Uint8Array): string => {
    let h1 = 0x811c9dc5 | 0;
    let h2 = 0x01000193 | 0;
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i];
        h1 = Math.imul(h1 ^ b, 0x01000193);
        h2 = Math.imul(h2 + b, 0x85ebca6b) ^ (h2 >>> 13);
    }
    const hex = (n: number) => (n >>> 0).toString(16).padStart(8, '0');
    return hex(h1) + hex(h2);
};

const hashFile = async (file: File): Promise<string> => {
    // Only sample the first 1 MB to avoid the 2 GB ArrayBuffer limit in SubtleCrypto.
    // Append the full file size so files with identical first 1 MB are still distinguished.
    const SAMPLE = 1024 * 1024;
    const sampleBuffer = await file.slice(0, SAMPLE).arrayBuffer();
    const combined = new Uint8Array(sampleBuffer.byteLength + 8);
    combined.set(new Uint8Array(sampleBuffer));
    const view = new DataView(combined.buffer);
    view.setBigUint64(sampleBuffer.byteLength, BigInt(file.size), false);

    // crypto.subtle only exists in a secure context (HTTPS, or localhost/127.0.0.1).
    // Served over plain HTTP from a LAN IP it is undefined, which used to throw here
    // and abort the whole import. This hash is only ever an identity key for restoring
    // per-file evidence data, never a security primitive, so degrade instead of failing.
    if (!crypto.subtle) {
        return fallbackHash(combined);
    }

    const digest = await crypto.subtle.digest('SHA-256', combined);
    return Array.from(new Uint8Array(digest))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('')
        .slice(0, 16);
};

const registerBlgImportEvents = (events: Events) => {

    const showLoadError = async (message: string) => {
        await events.invoke('showPopup', {
            type: 'error',
            header: localize('popup.error-loading'),
            message: message
        });
    };

    const loadBlg = async (file: File) => {
        events.fire('samples.hide');
        events.fire('startSpinner');
        let zipReader;
        let loadError: string | null = null;
        try {
            // Create a ZipReader with a BlobReader
            zipReader = new zip.ZipReader(new zip.BlobReader(file));
            const entries = await zipReader.getEntries();

            const modelFiles: { filename: string, contents: File }[] = [];
            const imageFiles: File[] = [];
            let poseFile: File | null = null;
            let markerBoxesJson: string | null = null;
            let foundAny = false;

            for (const entry of entries) {
                if (entry.directory) continue;

                const filename = entry.filename;

                // Strict structure check matches previous implementation

                if (filename === 'gaussian.ply' || filename === 'gaussian.splat') {
                    const blob = await entry.getData(new zip.BlobWriter());
                    const f = new File([blob], filename);
                    modelFiles.push({ filename, contents: f });
                    foundAny = true;
                } else if (filename === 'images.json') {
                    const blob = await entry.getData(new zip.BlobWriter());
                    poseFile = new File([blob], filename);
                    foundAny = true;
                } else if (filename === 'marker_boxes_3d.json') {
                    const blob = await entry.getData(new zip.BlobWriter());
                    markerBoxesJson = await blob.text();
                } else if (filename.startsWith('images/') && isImageFile(filename)) {
                    const blob = await entry.getData(new zip.BlobWriter());
                    const simpleName = filename.split('/').pop();
                    const f = new File([blob], simpleName, { type: 'image/' + filename.split('.').pop() });
                    imageFiles.push(f);
                    foundAny = true;
                }
            }

            // Load Models
            if (modelFiles.length > 0) {
                await events.invoke('import', modelFiles);
            }

            // Load Images
            if (imageFiles.length > 0) {
                await events.invoke('images.load', imageFiles);
            }

            // Load Poses
            if (poseFile) {
                await events.invoke('poses.load', poseFile);
            }

            // Load marker boxes (optional)
            if (markerBoxesJson) {
                try {
                    const data = JSON.parse(markerBoxesJson);
                    if (Array.isArray(data.boxes)) {
                        // Clear any zones from a previous load
                        const existingIds: string[] = await events.invoke('clickableZones.list');
                        for (const id of existingIds) {
                            events.fire('clickableZones.remove', id);
                        }
                        for (const box of data.boxes) {
                            const [cx, cy, cz] = box.center_xyz;
                            const [minX, minY, minZ] = box.bbox_min_xyz;
                            const [maxX, maxY, maxZ] = box.bbox_max_xyz;
                            // Match splat entity's setEulerAngles(0,0,180): 180° Z-rotation maps (x,y,z)→(-x,-y,z)
                            events.invoke('clickableZones.add', {
                                id: box.box_id,
                                position: new Vec3(-cx, -cy, cz),
                                halfExtents: new Vec3(
                                    (maxX - minX) / 2,
                                    (maxY - minY) / 2,
                                    (maxZ - minZ) / 2
                                ),
                                label: `Marker ${box.marker_id}`,
                                supportingImages: box.supporting_images ?? [],
                            });
                        }
                    }
                } catch (e) {
                    console.warn('[BlgImport] Failed to parse marker_boxes_3d.json:', e);
                }
            }

            // Always fire after all content is loaded so the evidence panel
            // can restore per-file runtime data regardless of marker box presence
            events.fire('blg.loaded', await hashFile(file));
            events.fire('evidencePanel.refresh');

            if (!foundAny) {
                loadError = 'Invalid .blgs structure. Expected gaussian.ply, images.json in root, and images in images/ folder.';
            }

        } catch (err: any) {
            console.error(err);
            loadError = `${err.message || 'Failed to read .blgs file'}`;
        } finally {
            if (zipReader) {
                await zipReader.close();
            }
            events.fire('stopSpinner');
            // Tear down any progress overlay owned by our caller before raising a modal
            // popup. The overlay covers the viewport and swallows pointer events, so a
            // popup shown beneath it can never be dismissed - which would deadlock the
            // await below and leave the app stuck on the progress bar forever.
            events.fire('progressEnd');
        }

        // Deliberately outside the try/finally: showing this while the overlays were
        // still up is what caused the hang described above.
        if (loadError) {
            await showLoadError(loadError);
        }
    };

    // modern picker
    events.function('blg.open', async () => {
        try {
            if ((window as any).showOpenFilePicker) {
                const handles = await (window as any).showOpenFilePicker({
                    id: 'BlgFileOpen',
                    multiple: false,
                    types: [{
                        description: 'Biligoba Project File',
                        accept: {
                            'application/zip': ['.blgs'],
                            'application/octet-stream': ['.blgs']
                        }
                    }]
                });

                if (handles && handles.length > 0) {
                    const file = await handles[0].getFile();
                    await loadBlg(file);
                }
            } else {
                // simple fallback
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.blgs';
                input.onchange = async () => {
                    if (input.files && input.files.length > 0) {
                        await loadBlg(input.files[0]);
                    }
                };
                input.click();
            }
        } catch (err: any) {
            if (err.name !== 'AbortError') {
                console.error(err);
            }
        }
    });

    events.function('blg.load', async (file: File) => {
        await loadBlg(file);
    });
};

export { registerBlgImportEvents };
